import { useState, useEffect, useRef } from 'react';
import './NavigationRibbon.scss';
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { IconControl, ContextMenuControl } from 'spark-core-dx/components';
import { GlobalFilterPanel } from '../GlobalFilter/GlobalFilterPanel.jsx';
import DashboardCreateDialog from '../dialogs/DashboardCreateDialog.jsx';
import CloseAllDialog from '../dialogs/CloseAllDialog.jsx';
import OpenDashboardDialog from '../dialogs/OpenDashboardDialog.jsx';
import ClearAllDialog from '../dialogs/ClearAllDialog.jsx';
import TabWrapper from './TabWrapper.jsx';
import DeleteDialog from '../dialogs/DeleteDialog.jsx';
import RevertChangesDialog from '../dialogs/RevertChangesDialog.jsx';
import PublishDialog from '../dialogs/PublishDialog.jsx';
import ExitDialog from '../dialogs/ExitDialog.jsx';
import { useDashboardSourceDataContext } from '../../DashboardSourceDataProvider.js';
import { useCorePermissions } from 'spark-core-dx/hooks';

const NavigationRibbon = () => {
  const {
    dashboardUserNavStateLib,
    dashboardUserStateLib,
    dashboardLib,
    setIsFilterFlyoutOpen,
    isFilterFlyoutOpen,
    switchMode,
    isViewMode,
    isOwner,
  } = useDashboardDomainContext();

  const { canEdit } = useCorePermissions();
  const { clearSourceDataCache } = useDashboardSourceDataContext();

  const [dashboardContextMenu, setDashboardContextMenu] = useState(false);
  const [dashboardContextStyle, setDashboardContextStyle] = useState({});
  const [tabContextMenu, setTabContextMenu] = useState(false);
  const [tabContextMenuStyle, setTabContextMenuStyle] = useState();

  const [dialogType, setDialogType] = useState("");

  const useHorizontalScroll = () => {
    const elRef = useRef();

    useEffect(() => {
      const el = elRef.current;
      if (!el) return;

      let scrollTimeout;

      const onWheel = (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();

        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
          behavior: 'smooth',
        });

        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          dashboardUserNavStateLib.updateScrollPosition(
            Math.round(el.scrollLeft)
          );
        }, 150);
      };

      el.addEventListener("wheel", onWheel, { passive: false });
      return () => {
        clearTimeout(scrollTimeout);
        el.removeEventListener("wheel", onWheel);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardUserNavStateLib.updateScrollPosition]);

    return elRef;
  };

  const scrollRef = useHorizontalScroll(
    dashboardUserNavStateLib.updateScrollPosition
  );

  // Filter flyout button click handler
  const handleFilterButtonClick = () => {
    if (dashboardContextMenu) setDashboardContextMenu(false);
    setIsFilterFlyoutOpen(!isFilterFlyoutOpen);
  };

  const handleOpenContext = () => setDialogType("open");
  const handleCloseContext = () => setDialogType("closeall");
  const handleCreateButton = () => {
    if (canEdit('Dashboard')) setDialogType("create");
  };
  const handlePublishButton = () => setDialogType("publish");

  const handleContextMenu = (e, tabId) => {
    const iconBox = e.currentTarget?.getBoundingClientRect?.();
    const tabBox = e.currentTarget?.closest?.('.tab')?.getBoundingClientRect?.();
    const containerEl = document.querySelector('.dashboard-navigation');
    const container = containerEl?.getBoundingClientRect?.();

    if (tabId) {
      const scrollContainer = scrollRef?.current?.getBoundingClientRect?.();
      setTabContextMenu(tabId);

      if (tabBox && scrollContainer) {
        setTabContextMenuStyle({
          position: 'absolute',
          bottom: "3rem",
          left:
            (tabBox.left - scrollContainer.left) +
            (tabBox.width / 2) +
            2 /* border fudge */,
        });
      } else {
        // Fallback: pin under the click target
        setTabContextMenuStyle({
          position: 'fixed',
          top: (iconBox?.bottom ?? 0) + 6,
          left: (iconBox?.left ?? 0),
        });
      }
    } else {
      setDashboardContextMenu(!dashboardContextMenu);
      setDashboardContextStyle({
        left: iconBox?.left ?? 0,
        bottom: container ? (window.innerHeight - container.top) : 64,
        position: 'fixed',
      });
    }
  };

  const closeDialog = () => setDialogType("");

  const renderContextMenuOptions = (optionList) => (
    <div className="options-wrapper">
      {optionList.map((opt) => (
        <div
          key={opt.text}
          className={`option${opt.disabled ? ' disabled' : ''}`}
          onClick={!opt.disabled ? opt.onClick : undefined}
        >
          <IconControl iconKey={opt.icon} />
          {opt.text}
        </div>
      ))}
    </div>
  );

  // Compare Published vs Draft (PascalCase now)
  const equalStates = dashboardLib?.activeDashboard?.PublishedDetails
    ? JSON.stringify(dashboardLib?.activeDashboard?.PublishedDetails) ===
      JSON.stringify(dashboardLib?.activeDashboard?.DraftDetails)
    : false;


  const tabContextMenuOptions = [
    ...(isOwner && canEdit('Dashboard')
      ? [
          {
            text: "Design",
            icon: "edit",
            onClick: () => {
              switchMode("design");
              setTabContextMenu(false);
              dashboardUserNavStateLib.selectDashboard(tabContextMenu);
            },
          },
        ]
      : []),
    {
      text: "Close",
      icon: "close",
      onClick: () => {
        setTabContextMenu(false);
        dashboardUserNavStateLib.closeDashboard(tabContextMenu);

        if (dashboardUserNavStateLib?.dashboardTabs.length === 1) {
          // If this is the last tab, close the filter flyout as well
          setIsFilterFlyoutOpen(false);
        }

        if (dashboardUserNavStateLib?.selectedTab === tabContextMenu) {
          setTimeout(() => {
            scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
          }, 0);
        }
      },
    },
  ];

  // Context menu options differ by mode
  const dashboardContextMenuOptions = isViewMode ? 
      [
        {
          text: "Open",
          icon: "fileopen",
          onClick: handleOpenContext,
        },
        {
          text: "Close All",
          icon: "close",
          disabled: dashboardUserNavStateLib?.dashboardTabs.length === 0,
          onClick: handleCloseContext,
        },
      ]
    : [
        {
          text: "Delete",
          icon: "delete",
          onClick: () => setDialogType("delete"),
        },
        {
          text: "Clear All Visuals",
          icon: "clearall",
          // Disable if no visuals
          disabled:
            (dashboardLib?.activeDashboard?.DraftDetails?.Visuals?.length ?? 0) ===
            0,
          onClick: () => setDialogType("clearall"),
        },
        {
          text: "Revert Changes",
          icon: "undo",
          // Disabled if no published state or states already match
          disabled: !dashboardLib?.activeDashboard?.State?.IsPublished || equalStates,
          onClick: () => setDialogType("revertchanges"),
        },
        {
          text: "Exit Design Mode",
          icon: "close",
          onClick: equalStates
            ? () => {
                switchMode("view");
                clearSourceDataCache();
                setDashboardContextMenu(false);
              }
            : () => setDialogType("exit"),
        },
      ];

  return (
    <div className="dashboard-navigation">
      <DashboardCreateDialog isActive={dialogType === "create"} closeDialog={closeDialog} />
      <CloseAllDialog isActive={dialogType === "closeall"} closeDialog={closeDialog} />
      <OpenDashboardDialog scrollRef={scrollRef} isActive={dialogType === "open"} closeDialog={closeDialog} />
      <ClearAllDialog isActive={dialogType === "clearall"} closeDialog={closeDialog} />
      <DeleteDialog isActive={dialogType === "delete"} closeDialog={closeDialog} />
      <RevertChangesDialog isActive={dialogType === "revertchanges"} closeDialog={closeDialog} />
      <PublishDialog isActive={dialogType === "publish"} closeDialog={closeDialog} />
      <ExitDialog scrollRef={scrollRef} isActive={dialogType === "exit"} closeDialog={closeDialog} />

      {tabContextMenu && (
        <ContextMenuControl
          openContextMenu={tabContextMenu}
          setOpenContextMenu={setTabContextMenu}
          contextMenuStyle={tabContextMenuStyle}
        >
          {renderContextMenuOptions(tabContextMenuOptions)}
        </ContextMenuControl>
      )}

      {dashboardContextMenu && (
        <ContextMenuControl
          openContextMenu={dashboardContextMenu}
          setOpenContextMenu={setDashboardContextMenu}
          contextMenuStyle={dashboardContextStyle}
        >
          {renderContextMenuOptions(dashboardContextMenuOptions)}
        </ContextMenuControl>
      )}

      <div className="menu-buttons left">
        {isViewMode ? (
          <IconControl
            className={`menu-button-icon large ${!canEdit('Dashboard') ? 'disabled' : ''}`}
            iconKey={"add"}
            onClick={handleCreateButton}
          />
        ) : (
          <IconControl className={"menu-button-icon"} iconKey={"upload"} onClick={handlePublishButton} />
        )}
        <IconControl
          className={`menu-button-icon filled`}
          iconKey={"dashboard"}
          onClick={(e) => handleContextMenu(e)}
        />
      </div>

      {isViewMode ? (
        <>
          <TabWrapper scrollRef={scrollRef} handleContextMenu={handleContextMenu} />

          <div
            className={`menu-buttons right ${
              !dashboardUserNavStateLib?.dashboardTabs?.length ? 'disabled' : ''
            }`}
          >
            <IconControl
              onClick={handleFilterButtonClick}
              className={`menu-button-icon ${
                dashboardUserStateLib?.activeUserDashState?.GlobalFilters ? 'active filled' : ''
              }`}
              iconKey={"configurefilter"}
            />
          </div>

          <GlobalFilterPanel
            open={isFilterFlyoutOpen}
            title={"Dashboard Filters"}
            setOpenEditSidePanel={setIsFilterFlyoutOpen}
            metadata={dashboardUserNavStateLib?.dashboardTabs}
            onUpdateSidePanel={() => {}}
          />
        </>
      ) : (
        <div className="design-mode">Design Mode</div>
      )}
    </div>
  );
};

export default NavigationRibbon;
